import { c as _c } from "react/compiler-runtime";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type SmsChannelHealth, type SmsTestResult } from "@/api";
import { smsKeys, useSmsHealth, useSmsLog } from "@/api/hooks/use-sms";
import { IntegrationsOverviewSection } from "@/components/integrations-overview";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { formatDateTime } from "@/lib/utils";
import { useAuth } from "@/store";
const STATUS_FILTERS = ["all", "sent", "delivered", "failed", "skipped"] as const;
const statusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => status === "delivered" ? "default" : status === "failed" ? "destructive" : status === "skipped" ? "outline" : "secondary";
const money = (value: number, currency: string) => `${value.toLocaleString("ru-RU", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})} ${currency === "RUR" || currency === "RUB" ? "руб." : currency}`;

/**
 * Раздел «Интеграции» → канал SMS (СМС-Центр).
 *
 * Отвечает на четыре вопроса, которые возникают, когда вход по коду перестаёт
 * работать: подключён ли канал, есть ли деньги на счёте, ушло ли конкретное
 * сообщение и что ответил оператор. Пробная отправка по умолчанию идёт
 * «вхолостую» (СМСЦ считает стоимость, но ничего не отправляет) — проверить
 * реквизиты можно, не тратя ни рубля и не беспокоя абонента.
 */
export function IntegrationsPage() {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuth(s => s.user);
  const canManage = hasAdminPermission(user, "action.integrations.manage");
  const {
    data: health,
    error: healthError
  } = useSmsHealth();
  const [phoneFilter, setPhoneFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const {
    data: log
  } = useSmsLog({
    phone: phoneFilter,
    status: statusFilter,
    limit: 50
  });
  const [testPhone, setTestPhone] = useState("");
  const [realSend, setRealSend] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<SmsTestResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const reload = () => {
    queryClient.invalidateQueries({
      queryKey: smsKeys.health
    });
    queryClient.invalidateQueries({
      queryKey: ["sms", "log"]
    });
  };
  const runTest = async () => {
    setTesting(true);
    setActionError(null);
    setTestResult(null);
    try {
      setTestResult(await api.sms.test({
        phone: testPhone.trim(),
        dryRun: !realSend
      }));
      if (realSend) reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setTesting(false);
    }
  };
  const refreshStatuses = async () => {
    setRefreshing(true);
    setActionError(null);
    try {
      await api.sms.refreshStatuses();
      reload();
    } catch (e_0) {
      setActionError(e_0 instanceof Error ? e_0.message : String(e_0));
    } finally {
      setRefreshing(false);
    }
  };
  return <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1" data-tour="integrations-intro">
        <h1 className="text-2xl font-bold">{t("nav.integrations")}</h1>
        <p className="text-sm text-muted-foreground">{t("integrations.intro")}</p>
      </div>

      {/* Все внешние сервисы одним экраном: кто реально принимает оплату,
          что подключено, чего не хватает, когда последний раз работало. Раньше
          раздел знал SMS и ещё пять каналов, а Робокассы не видел вовсе. */}
      <IntegrationsOverviewSection />

      <h2 id="sms" className="scroll-mt-20 pt-2 text-lg font-semibold">
        {t("integrations.smsHeading")}
      </h2>

      {actionError && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {actionError}
        </div>}

      {healthError ? <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {healthError.message}
        </div> : !health ? <p className="text-muted-foreground">{t("integrations.loading")}</p> : <SmsChannel health={health} canManage={canManage} testPhone={testPhone} setTestPhone={setTestPhone} realSend={realSend} setRealSend={setRealSend} testing={testing} runTest={runTest} refreshing={refreshing} refreshStatuses={refreshStatuses} testResult={testResult} />}

      <section className="flex flex-col gap-4 rounded-lg border p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">
            {t("integrations.logHeading")}
          </h2>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="log-phone">{t("integrations.filterPhone")}</Label>
              <Input id="log-phone" value={phoneFilter} onChange={e_1 => setPhoneFilter(e_1.target.value)} placeholder="+7999" />
            </div>
            <div className="flex flex-wrap gap-1">
              {STATUS_FILTERS.map(status => <Button key={status} size="sm" variant={statusFilter === status ? "default" : "outline"} onClick={() => setStatusFilter(status)}>
                  {t(`integrations.status.${status}`)}
                </Button>)}
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">{t("integrations.logPrivacyNote")}</p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-2 pr-4 font-medium">{t("integrations.table.time")}</th>
                <th className="py-2 pr-4 font-medium">{t("integrations.table.phone")}</th>
                <th className="py-2 pr-4 font-medium">{t("integrations.table.purpose")}</th>
                <th className="py-2 pr-4 font-medium">{t("integrations.table.text")}</th>
                <th className="py-2 pr-4 font-medium">{t("integrations.table.status")}</th>
                <th className="py-2 pr-4 font-medium">{t("integrations.table.cost")}</th>
              </tr>
            </thead>
            <tbody>
              {(log?.items ?? []).map(item => <tr key={item.id} className="border-t">
                  <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">
                    {formatDateTime(item.createdAt)}
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">{item.phone}</td>
                  <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">
                    {t(`integrations.purpose.${item.purpose}`, {
                  defaultValue: item.purpose
                })}
                  </td>
                  <td className="py-2 pr-4">{item.textPreview}</td>
                  <td className="py-2 pr-4">
                    <Badge variant={statusVariant(item.status)}>
                      {item.providerStatusName || item.error || t(`integrations.status.${item.status}`)}
                    </Badge>
                  </td>
                  <td className="py-2 pr-4 whitespace-nowrap">
                    {money(item.cost, health?.currency ?? "RUB")}
                  </td>
                </tr>)}
              {(log?.items.length ?? 0) === 0 && <tr>
                  <td colSpan={6} className="py-6 text-center text-muted-foreground">
                    {t("integrations.logEmpty")}
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </section>

      <IntegrationEventsSection />

      <PartnerApiKeysSection canManage={canManage} />
    </div>;
}

/**
 * API-ключи магазинов (пакет П5, fd-platform-01): кто выпустил ключ публичного
 * API, с какими правами, когда им последний раз пользовались. Отзыв — с правом
 * action.integrations.manage; вызовы по ключам видны в журнале выше
 * (поставщик «API продавцов»).
 */
function PartnerApiKeysSection(t0) {
  const $ = _c(99);
  const {
    canManage
  } = t0;
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const [shopId, setShopId] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [error, setError] = useState(null);
  let t1;
  if ($[0] !== activeOnly || $[1] !== shopId) {
    t1 = {
      queryKey: ["integrations", "api-keys", shopId, activeOnly],
      queryFn: () => api.integrations.apiKeys({
        shopId: shopId.trim() || undefined,
        active: activeOnly
      })
    };
    $[0] = activeOnly;
    $[1] = shopId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const keys = useQuery(t1);
  let t2;
  if ($[3] !== queryClient || $[4] !== t) {
    t2 = async (id, name, shop) => {
      if (!confirm(t("integrations.apiKeys.confirmRevoke", {
        name,
        shop
      }))) {
        return;
      }
      setError(null);
      ;
      try {
        await api.integrations.revokeApiKey(id);
        await queryClient.invalidateQueries({
          queryKey: ["integrations", "api-keys"]
        });
      } catch (t3) {
        const e = t3;
        setError(e instanceof Error ? e.message : t("integrations.apiKeys.error"));
      }
    };
    $[3] = queryClient;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const revoke = t2;
  let t3;
  if ($[6] !== t) {
    t3 = t("integrations.apiKeys.title");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <h2 className="text-lg font-semibold">{t3}</h2>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("integrations.apiKeys.intro");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <p className="text-sm text-muted-foreground">{t5}</p>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t6) {
    t7 = <div className="flex flex-col gap-1">{t4}{t6}</div>;
    $[14] = t4;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t) {
    t8 = t("integrations.apiKeys.shop");
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t8) {
    t9 = <Label htmlFor="api-keys-shop">{t8}</Label>;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t) {
    t10 = t("integrations.apiKeys.shopPlaceholder");
    $[21] = t;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_0 => setShopId(e_0.target.value);
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== shopId || $[25] !== t10) {
    t12 = <Input id="api-keys-shop" value={shopId} placeholder={t10} onChange={t11} />;
    $[24] = shopId;
    $[25] = t10;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t9) {
    t13 = <div className="flex flex-col gap-1">{t9}{t12}</div>;
    $[27] = t12;
    $[28] = t9;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = e_1 => setActiveOnly(e_1.target.checked);
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== activeOnly) {
    t15 = <input type="checkbox" checked={activeOnly} onChange={t14} />;
    $[31] = activeOnly;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t) {
    t16 = t("integrations.apiKeys.activeOnly");
    $[33] = t;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16) {
    t17 = <label className="flex h-9 items-center gap-2 text-sm">{t15}{t16}</label>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t13 || $[39] !== t17) {
    t18 = <div className="flex flex-wrap items-end gap-3">{t13}{t17}</div>;
    $[38] = t13;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== error) {
    t19 = error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>;
    $[41] = error;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t) {
    t20 = t("integrations.apiKeys.shop");
    $[43] = t;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t20) {
    t21 = <th className="p-2 font-medium">{t20}</th>;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t) {
    t22 = t("integrations.apiKeys.name");
    $[47] = t;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t22) {
    t23 = <th className="p-2 font-medium">{t22}</th>;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t) {
    t24 = t("integrations.apiKeys.prefix");
    $[51] = t;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t24) {
    t25 = <th className="p-2 font-medium">{t24}</th>;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== t) {
    t26 = t("integrations.apiKeys.scopes");
    $[55] = t;
    $[56] = t26;
  } else {
    t26 = $[56];
  }
  let t27;
  if ($[57] !== t26) {
    t27 = <th className="p-2 font-medium">{t26}</th>;
    $[57] = t26;
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t) {
    t28 = t("integrations.apiKeys.created");
    $[59] = t;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t28) {
    t29 = <th className="p-2 font-medium">{t28}</th>;
    $[61] = t28;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  let t30;
  if ($[63] !== t) {
    t30 = t("integrations.apiKeys.lastUsed");
    $[63] = t;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== t30) {
    t31 = <th className="p-2 font-medium">{t30}</th>;
    $[65] = t30;
    $[66] = t31;
  } else {
    t31 = $[66];
  }
  let t32;
  if ($[67] !== t) {
    t32 = t("integrations.apiKeys.status");
    $[67] = t;
    $[68] = t32;
  } else {
    t32 = $[68];
  }
  let t33;
  if ($[69] !== t32) {
    t33 = <th className="p-2 font-medium">{t32}</th>;
    $[69] = t32;
    $[70] = t33;
  } else {
    t33 = $[70];
  }
  let t34;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = <th />;
    $[71] = t34;
  } else {
    t34 = $[71];
  }
  let t35;
  if ($[72] !== t21 || $[73] !== t23 || $[74] !== t25 || $[75] !== t27 || $[76] !== t29 || $[77] !== t31 || $[78] !== t33) {
    t35 = <thead className="bg-muted/50 text-left"><tr>{t21}{t23}{t25}{t27}{t29}{t31}{t33}{t34}</tr></thead>;
    $[72] = t21;
    $[73] = t23;
    $[74] = t25;
    $[75] = t27;
    $[76] = t29;
    $[77] = t31;
    $[78] = t33;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  let t36;
  if ($[80] !== canManage || $[81] !== keys.data || $[82] !== revoke || $[83] !== t) {
    t36 = keys.data?.map(key => <tr key={key.id} className="border-t align-top"><td className="p-2">{key.shopName || key.shopId}<span className="block text-xs text-muted-foreground">{key.shopId}</span></td><td className="p-2 font-medium">{key.name}</td><td className="p-2 font-mono text-xs">{key.prefix}…</td><td className="p-2"><div className="flex flex-wrap gap-1">{key.scopes.map(_temp)}</div></td><td className="whitespace-nowrap p-2 text-muted-foreground">{formatDateTime(key.createdAt)}</td><td className="whitespace-nowrap p-2 text-muted-foreground">{key.lastUsedAt ? formatDateTime(key.lastUsedAt) : t("integrations.apiKeys.never")}</td><td className="p-2"><Badge variant={key.revokedAt ? "secondary" : "default"}>{key.revokedAt ? t("integrations.apiKeys.revoked") : t("integrations.apiKeys.active")}</Badge></td><td className="p-2 text-right">{canManage && !key.revokedAt && <Button variant="outline" size="sm" onClick={() => revoke(key.id, key.name, key.shopName || key.shopId)}>{t("integrations.apiKeys.revoke")}</Button>}</td></tr>);
    $[80] = canManage;
    $[81] = keys.data;
    $[82] = revoke;
    $[83] = t;
    $[84] = t36;
  } else {
    t36 = $[84];
  }
  let t37;
  if ($[85] !== keys.data?.length || $[86] !== t) {
    t37 = (keys.data?.length ?? 0) === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">{t("integrations.apiKeys.empty")}</td></tr>;
    $[85] = keys.data?.length;
    $[86] = t;
    $[87] = t37;
  } else {
    t37 = $[87];
  }
  let t38;
  if ($[88] !== t36 || $[89] !== t37) {
    t38 = <tbody>{t36}{t37}</tbody>;
    $[88] = t36;
    $[89] = t37;
    $[90] = t38;
  } else {
    t38 = $[90];
  }
  let t39;
  if ($[91] !== t35 || $[92] !== t38) {
    t39 = <div className="overflow-x-auto rounded-md border"><table className="w-full text-sm">{t35}{t38}</table></div>;
    $[91] = t35;
    $[92] = t38;
    $[93] = t39;
  } else {
    t39 = $[93];
  }
  let t40;
  if ($[94] !== t18 || $[95] !== t19 || $[96] !== t39 || $[97] !== t7) {
    t40 = <section id="api-keys" className="flex scroll-mt-20 flex-col gap-4 rounded-lg border p-5">{t7}{t18}{t19}{t39}</section>;
    $[94] = t18;
    $[95] = t19;
    $[96] = t39;
    $[97] = t7;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  return t40;
}

/**
 * Журнал обращений к внешним системам.
 *
 * Отвечает на вопрос, который до миграции 083 разбирать было нечем: покупатель
 * говорит, что заплатил, а заказ висит неоплаченным — приходил ли вебхук, что в
 * нём было и чем кончился его разбор. Плюс исходящие вызовы: регистрация
 * отправления в СДЭК и возврат денег.
 */
function _temp(scope) {
  return <Badge key={scope} variant="outline">{scope}</Badge>;
}
function IntegrationEventsSection() {
  const $ = _c(126);
  const {
    t
  } = useTranslation();
  const [provider, setProvider] = useState("all");
  const [status, setStatus] = useState("all");
  const [refId, setRefId] = useState("");
  const [opened, setOpened] = useState(null);
  let t0;
  if ($[0] !== provider || $[1] !== refId || $[2] !== status) {
    t0 = {
      queryKey: ["integrations", "events", provider, status, refId],
      queryFn: () => api.sms.events({
        provider,
        status,
        refId,
        limit: 100
      })
    };
    $[0] = provider;
    $[1] = refId;
    $[2] = status;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const events = useQuery(t0);
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["integrations", "events", "summary"],
      queryFn: _temp2,
      staleTime: 60000
    };
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const summary = useQuery(t1);
  let t2;
  if ($[5] !== t) {
    t2 = t("integrations.events.title");
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <h2 className="text-lg font-semibold">{t2}</h2>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t) {
    t4 = t("integrations.events.intro");
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <p className="text-sm text-muted-foreground">{t4}</p>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t5) {
    t6 = <div className="flex flex-col gap-1">{t3}{t5}</div>;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== summary.data || $[17] !== t) {
    t7 = (summary.data?.length ?? 0) > 0 && <div className="flex flex-wrap gap-2">{summary.data?.map(row => <Badge key={row.provider} variant={row.errors > 0 ? "destructive" : "secondary"}>{row.provider}: {row.total}{row.errors > 0 ? ` · ${t("integrations.events.errors", {
          count: row.errors
        })}` : ""}</Badge>)}</div>;
    $[16] = summary.data;
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t) {
    t8 = t("integrations.events.provider");
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <Label htmlFor="ev-provider">{t8}</Label>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e => setProvider(e.target.value);
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t) {
    t11 = t("integrations.events.any");
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11) {
    t12 = <option value="all">{t11}</option>;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <option value="robokassa">Робокасса</option>;
    t14 = <option value="cloudpayments">CloudPayments</option>;
    t15 = <option value="sdek">СДЭК</option>;
    t16 = <option value="pecom">ПЭК</option>;
    t17 = <option value="dellin">Деловые линии</option>;
    t18 = <option value="dadata">DaData</option>;
    t19 = <option value="smsc">СМС-Центр</option>;
    t20 = <option value="openrouter">OpenRouter</option>;
    t21 = <option value="s3">Хранилище S3</option>;
    t22 = <option value="partner_api">API продавцов</option>;
    t23 = <option value="moysklad">МойСклад</option>;
    t24 = <option value="onec">1С: обмен с сайтом</option>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
    $[34] = t19;
    $[35] = t20;
    $[36] = t21;
    $[37] = t22;
    $[38] = t23;
    $[39] = t24;
  } else {
    t13 = $[28];
    t14 = $[29];
    t15 = $[30];
    t16 = $[31];
    t17 = $[32];
    t18 = $[33];
    t19 = $[34];
    t20 = $[35];
    t21 = $[36];
    t22 = $[37];
    t23 = $[38];
    t24 = $[39];
  }
  let t25;
  if ($[40] !== provider || $[41] !== t12) {
    t25 = <select id="ev-provider" className="h-9 rounded-md border bg-background px-2 text-sm" value={provider} onChange={t10}>{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}{t24}</select>;
    $[40] = provider;
    $[41] = t12;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== t25 || $[44] !== t9) {
    t26 = <div className="flex flex-col gap-1">{t9}{t25}</div>;
    $[43] = t25;
    $[44] = t9;
    $[45] = t26;
  } else {
    t26 = $[45];
  }
  let t27;
  if ($[46] !== t) {
    t27 = t("integrations.events.status");
    $[46] = t;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== t27) {
    t28 = <Label htmlFor="ev-status">{t27}</Label>;
    $[48] = t27;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = e_0 => setStatus(e_0.target.value);
    $[50] = t29;
  } else {
    t29 = $[50];
  }
  let t30;
  if ($[51] !== t) {
    t30 = t("integrations.events.any");
    $[51] = t;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== t30) {
    t31 = <option value="all">{t30}</option>;
    $[53] = t30;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  let t33;
  let t34;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <option value="ok">ok</option>;
    t33 = <option value="error">error</option>;
    t34 = <option value="ignored">ignored</option>;
    $[55] = t32;
    $[56] = t33;
    $[57] = t34;
  } else {
    t32 = $[55];
    t33 = $[56];
    t34 = $[57];
  }
  let t35;
  if ($[58] !== status || $[59] !== t31) {
    t35 = <select id="ev-status" className="h-9 rounded-md border bg-background px-2 text-sm" value={status} onChange={t29}>{t31}{t32}{t33}{t34}</select>;
    $[58] = status;
    $[59] = t31;
    $[60] = t35;
  } else {
    t35 = $[60];
  }
  let t36;
  if ($[61] !== t28 || $[62] !== t35) {
    t36 = <div className="flex flex-col gap-1">{t28}{t35}</div>;
    $[61] = t28;
    $[62] = t35;
    $[63] = t36;
  } else {
    t36 = $[63];
  }
  let t37;
  if ($[64] !== t) {
    t37 = t("integrations.events.order");
    $[64] = t;
    $[65] = t37;
  } else {
    t37 = $[65];
  }
  let t38;
  if ($[66] !== t37) {
    t38 = <Label htmlFor="ev-ref">{t37}</Label>;
    $[66] = t37;
    $[67] = t38;
  } else {
    t38 = $[67];
  }
  let t39;
  if ($[68] !== t) {
    t39 = t("integrations.events.orderPlaceholder");
    $[68] = t;
    $[69] = t39;
  } else {
    t39 = $[69];
  }
  let t40;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = e_1 => setRefId(e_1.target.value);
    $[70] = t40;
  } else {
    t40 = $[70];
  }
  let t41;
  if ($[71] !== refId || $[72] !== t39) {
    t41 = <Input id="ev-ref" value={refId} placeholder={t39} onChange={t40} />;
    $[71] = refId;
    $[72] = t39;
    $[73] = t41;
  } else {
    t41 = $[73];
  }
  let t42;
  if ($[74] !== t38 || $[75] !== t41) {
    t42 = <div className="flex flex-col gap-1">{t38}{t41}</div>;
    $[74] = t38;
    $[75] = t41;
    $[76] = t42;
  } else {
    t42 = $[76];
  }
  let t43;
  if ($[77] !== t26 || $[78] !== t36 || $[79] !== t42) {
    t43 = <div className="flex flex-wrap items-end gap-3">{t26}{t36}{t42}</div>;
    $[77] = t26;
    $[78] = t36;
    $[79] = t42;
    $[80] = t43;
  } else {
    t43 = $[80];
  }
  let t44;
  if ($[81] !== t) {
    t44 = t("integrations.events.when");
    $[81] = t;
    $[82] = t44;
  } else {
    t44 = $[82];
  }
  let t45;
  if ($[83] !== t44) {
    t45 = <th className="p-2 font-medium">{t44}</th>;
    $[83] = t44;
    $[84] = t45;
  } else {
    t45 = $[84];
  }
  let t46;
  if ($[85] !== t) {
    t46 = t("integrations.events.provider");
    $[85] = t;
    $[86] = t46;
  } else {
    t46 = $[86];
  }
  let t47;
  if ($[87] !== t46) {
    t47 = <th className="p-2 font-medium">{t46}</th>;
    $[87] = t46;
    $[88] = t47;
  } else {
    t47 = $[88];
  }
  let t48;
  if ($[89] !== t) {
    t48 = t("integrations.events.event");
    $[89] = t;
    $[90] = t48;
  } else {
    t48 = $[90];
  }
  let t49;
  if ($[91] !== t48) {
    t49 = <th className="p-2 font-medium">{t48}</th>;
    $[91] = t48;
    $[92] = t49;
  } else {
    t49 = $[92];
  }
  let t50;
  if ($[93] !== t) {
    t50 = t("integrations.events.order");
    $[93] = t;
    $[94] = t50;
  } else {
    t50 = $[94];
  }
  let t51;
  if ($[95] !== t50) {
    t51 = <th className="p-2 font-medium">{t50}</th>;
    $[95] = t50;
    $[96] = t51;
  } else {
    t51 = $[96];
  }
  let t52;
  if ($[97] !== t) {
    t52 = t("integrations.events.status");
    $[97] = t;
    $[98] = t52;
  } else {
    t52 = $[98];
  }
  let t53;
  if ($[99] !== t52) {
    t53 = <th className="p-2 font-medium">{t52}</th>;
    $[99] = t52;
    $[100] = t53;
  } else {
    t53 = $[100];
  }
  let t54;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <th />;
    $[101] = t54;
  } else {
    t54 = $[101];
  }
  let t55;
  if ($[102] !== t45 || $[103] !== t47 || $[104] !== t49 || $[105] !== t51 || $[106] !== t53) {
    t55 = <thead className="bg-muted/50 text-left"><tr>{t45}{t47}{t49}{t51}{t53}{t54}</tr></thead>;
    $[102] = t45;
    $[103] = t47;
    $[104] = t49;
    $[105] = t51;
    $[106] = t53;
    $[107] = t55;
  } else {
    t55 = $[107];
  }
  let t56;
  if ($[108] !== events.data || $[109] !== opened || $[110] !== t) {
    t56 = events.data?.map(event => <Fragment key={event.id}><tr className="border-t"><td className="whitespace-nowrap p-2 text-muted-foreground">{formatDateTime(event.createdAt)}</td><td className="p-2">{event.provider}<span className="ml-1 text-xs text-muted-foreground">{event.direction === "in" ? "\u2190" : "\u2192"}</span></td><td className="p-2 font-medium">{event.event}</td><td className="p-2 text-muted-foreground">{event.refId || "\u2014"}</td><td className="p-2"><Badge variant={event.status === "error" ? "destructive" : "secondary"}>{event.status}</Badge></td><td className="p-2 text-right"><Button variant="ghost" size="sm" onClick={() => setOpened(opened === event.id ? null : event.id)}>{t("integrations.events.payload")}</Button></td></tr>{opened === event.id && <tr key={`${event.id}-payload`}><td colSpan={6} className="bg-muted/30 p-2">{event.error && <p className="mb-2 text-sm text-destructive">{event.error}</p>}<pre className="max-h-64 overflow-auto text-xs">{JSON.stringify(event.payload, null, 2)}</pre></td></tr>}</Fragment>);
    $[108] = events.data;
    $[109] = opened;
    $[110] = t;
    $[111] = t56;
  } else {
    t56 = $[111];
  }
  let t57;
  if ($[112] !== events.data?.length || $[113] !== t) {
    t57 = (events.data?.length ?? 0) === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">{t("integrations.events.empty")}</td></tr>;
    $[112] = events.data?.length;
    $[113] = t;
    $[114] = t57;
  } else {
    t57 = $[114];
  }
  let t58;
  if ($[115] !== t56 || $[116] !== t57) {
    t58 = <tbody>{t56}{t57}</tbody>;
    $[115] = t56;
    $[116] = t57;
    $[117] = t58;
  } else {
    t58 = $[117];
  }
  let t59;
  if ($[118] !== t55 || $[119] !== t58) {
    t59 = <div className="overflow-x-auto rounded-md border"><table className="w-full text-sm">{t55}{t58}</table></div>;
    $[118] = t55;
    $[119] = t58;
    $[120] = t59;
  } else {
    t59 = $[120];
  }
  let t60;
  if ($[121] !== t43 || $[122] !== t59 || $[123] !== t6 || $[124] !== t7) {
    t60 = <section className="flex flex-col gap-4 rounded-lg border p-5" data-tour="integrations-events">{t6}{t7}{t43}{t59}</section>;
    $[121] = t43;
    $[122] = t59;
    $[123] = t6;
    $[124] = t7;
    $[125] = t60;
  } else {
    t60 = $[125];
  }
  return t60;
}
function _temp2() {
  return api.sms.eventsSummary();
}
type SmsChannelProps = {
  health: SmsChannelHealth;
  canManage: boolean;
  testPhone: string;
  setTestPhone: (value: string) => void;
  realSend: boolean;
  setRealSend: (value: boolean) => void;
  testing: boolean;
  runTest: () => void;
  refreshing: boolean;
  refreshStatuses: () => void;
  testResult: SmsTestResult | null;
};

/** Канал SMS: реквизиты, остаток, сводка за сутки и пробная отправка. */
function SmsChannel(t0) {
  const $ = _c(120);
  const {
    health,
    canManage,
    testPhone,
    setTestPhone,
    realSend,
    setRealSend,
    testing,
    runTest,
    refreshing,
    refreshStatuses,
    testResult
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== t) {
    t1 = t("integrations.stats.sent");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = String(health.stats.sent);
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      label: t1,
      value: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("integrations.stats.delivered");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = String(health.stats.delivered);
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = {
      label: t4,
      value: t5
    };
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t("integrations.stats.failed");
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = String(health.stats.failed);
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = {
      label: t7,
      value: t8
    };
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t) {
    t10 = t("integrations.stats.cost");
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== health.currency || $[18] !== health.stats.cost) {
    t11 = money(health.stats.cost, health.currency);
    $[17] = health.currency;
    $[18] = health.stats.cost;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10 || $[21] !== t11) {
    t12 = {
      label: t10,
      value: t11
    };
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t3 || $[25] !== t6 || $[26] !== t9) {
    t13 = [t3, t6, t9, t12];
    $[23] = t12;
    $[24] = t3;
    $[25] = t6;
    $[26] = t9;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const stats = t13;
  let t14;
  if ($[28] !== t) {
    t14 = t("integrations.channelHeading");
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14) {
    t15 = <h2 className="text-sm font-semibold uppercase text-muted-foreground">{t14}</h2>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const t16 = health.configured ? "default" : "destructive";
  let t17;
  if ($[32] !== health.configured || $[33] !== t) {
    t17 = health.configured ? t("integrations.connected") : t("integrations.notConfigured");
    $[32] = health.configured;
    $[33] = t;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t16 || $[36] !== t17) {
    t18 = <Badge variant={t16}>{t17}</Badge>;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== health.provider) {
    t19 = <span className="text-sm text-muted-foreground">{health.provider}</span>;
    $[38] = health.provider;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t15 || $[41] !== t18 || $[42] !== t19) {
    t20 = <div className="flex flex-wrap items-center gap-3">{t15}{t18}{t19}</div>;
    $[40] = t15;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t) {
    t21 = t("integrations.sender");
    $[44] = t;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t21) {
    t22 = <span className="text-xs text-muted-foreground">{t21}</span>;
    $[46] = t21;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== health.sender || $[49] !== t) {
    t23 = health.sender || t("integrations.senderMissing");
    $[48] = health.sender;
    $[49] = t;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== t23) {
    t24 = <span className="text-sm font-medium">{t23}</span>;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== t22 || $[54] !== t24) {
    t25 = <div className="flex flex-col gap-1">{t22}{t24}</div>;
    $[53] = t22;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = t("integrations.balance");
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t26) {
    t27 = <span className="text-xs text-muted-foreground">{t26}</span>;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  const t28 = `text-sm font-medium ${health.balanceLow ? "text-destructive" : ""}`;
  let t29;
  if ($[60] !== health.balance || $[61] !== health.currency || $[62] !== t) {
    t29 = health.balance === null ? t("integrations.balanceUnknown") : money(health.balance, health.currency);
    $[60] = health.balance;
    $[61] = health.currency;
    $[62] = t;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t28 || $[65] !== t29) {
    t30 = <span className={t28}>{t29}</span>;
    $[64] = t28;
    $[65] = t29;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t27 || $[68] !== t30) {
    t31 = <div className="flex flex-col gap-1">{t27}{t30}</div>;
    $[67] = t27;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== t) {
    t32 = t("integrations.window");
    $[70] = t;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== t32) {
    t33 = <span className="text-xs text-muted-foreground">{t32}</span>;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== health.stats.windowHours || $[75] !== t) {
    t34 = t("integrations.hours", {
      count: health.stats.windowHours
    });
    $[74] = health.stats.windowHours;
    $[75] = t;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] !== t34) {
    t35 = <span className="text-sm font-medium">{t34}</span>;
    $[77] = t34;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== t33 || $[80] !== t35) {
    t36 = <div className="flex flex-col gap-1">{t33}{t35}</div>;
    $[79] = t33;
    $[80] = t35;
    $[81] = t36;
  } else {
    t36 = $[81];
  }
  let t37;
  if ($[82] !== t25 || $[83] !== t31 || $[84] !== t36) {
    t37 = <div className="grid gap-4 sm:grid-cols-3">{t25}{t31}{t36}</div>;
    $[82] = t25;
    $[83] = t31;
    $[84] = t36;
    $[85] = t37;
  } else {
    t37 = $[85];
  }
  let t38;
  if ($[86] !== health.balanceLow || $[87] !== t) {
    t38 = health.balanceLow && <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{t("integrations.balanceLowWarning")}</p>;
    $[86] = health.balanceLow;
    $[87] = t;
    $[88] = t38;
  } else {
    t38 = $[88];
  }
  let t39;
  if ($[89] !== health.balanceError || $[90] !== t) {
    t39 = health.balanceError && <p className="text-sm text-muted-foreground">{t("integrations.balanceError", {
        error: health.balanceError
      })}</p>;
    $[89] = health.balanceError;
    $[90] = t;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== health.missingConfig || $[93] !== t) {
    t40 = health.missingConfig.length > 0 && <p className="text-sm text-muted-foreground">{t("integrations.missingConfig", {
        list: health.missingConfig.join(", ")
      })}</p>;
    $[92] = health.missingConfig;
    $[93] = t;
    $[94] = t40;
  } else {
    t40 = $[94];
  }
  let t41;
  if ($[95] !== stats) {
    t41 = <div className="grid gap-4 sm:grid-cols-4">{stats.map(_temp3)}</div>;
    $[95] = stats;
    $[96] = t41;
  } else {
    t41 = $[96];
  }
  let t42;
  if ($[97] !== t20 || $[98] !== t37 || $[99] !== t38 || $[100] !== t39 || $[101] !== t40 || $[102] !== t41) {
    t42 = <section className="flex flex-col gap-4 rounded-lg border p-5">{t20}{t37}{t38}{t39}{t40}{t41}</section>;
    $[97] = t20;
    $[98] = t37;
    $[99] = t38;
    $[100] = t39;
    $[101] = t40;
    $[102] = t41;
    $[103] = t42;
  } else {
    t42 = $[103];
  }
  let t43;
  if ($[104] !== canManage || $[105] !== health.currency || $[106] !== realSend || $[107] !== refreshStatuses || $[108] !== refreshing || $[109] !== runTest || $[110] !== setRealSend || $[111] !== setTestPhone || $[112] !== t || $[113] !== testPhone || $[114] !== testResult || $[115] !== testing) {
    t43 = canManage && <section className="flex flex-col gap-4 rounded-lg border p-5"><h2 className="text-sm font-semibold uppercase text-muted-foreground">{t("integrations.testHeading")}</h2><p className="text-sm text-muted-foreground">{t("integrations.testHint")}</p><div className="flex flex-wrap items-end gap-3"><div className="flex flex-col gap-1.5"><Label htmlFor="test-phone">{t("integrations.phone")}</Label><Input id="test-phone" placeholder="+79990001122" value={testPhone} onChange={e => setTestPhone(e.target.value)} /></div><label className="flex items-center gap-2 pb-2 text-sm"><input type="checkbox" checked={realSend} onChange={e_0 => setRealSend(e_0.target.checked)} />{t("integrations.realSend")}</label><Button onClick={runTest} disabled={testing || testPhone.trim().length < 5}>{testing ? t("integrations.testing") : t("integrations.runTest")}</Button><Button variant="outline" onClick={refreshStatuses} disabled={refreshing}>{refreshing ? t("integrations.refreshing") : t("integrations.refreshStatuses")}</Button></div>{testResult && <p className={`text-sm ${testResult.ok ? "text-muted-foreground" : "text-destructive"}`}>{testResult.ok ? testResult.dryRun ? t("integrations.testOkDry", {
          cost: money(testResult.cost, health.currency),
          parts: testResult.parts
        }) : t("integrations.testOkSent") : t("integrations.testFailed", {
          error: testResult.error,
          code: testResult.errorCode ?? "\u2014"
        })}</p>}</section>;
    $[104] = canManage;
    $[105] = health.currency;
    $[106] = realSend;
    $[107] = refreshStatuses;
    $[108] = refreshing;
    $[109] = runTest;
    $[110] = setRealSend;
    $[111] = setTestPhone;
    $[112] = t;
    $[113] = testPhone;
    $[114] = testResult;
    $[115] = testing;
    $[116] = t43;
  } else {
    t43 = $[116];
  }
  let t44;
  if ($[117] !== t42 || $[118] !== t43) {
    t44 = <>{t42}{t43}</>;
    $[117] = t42;
    $[118] = t43;
    $[119] = t44;
  } else {
    t44 = $[119];
  }
  return t44;
}
function _temp3(stat) {
  return <div key={stat.label} className="rounded-md border p-3"><div className="text-xs text-muted-foreground">{stat.label}</div><div className="text-lg font-semibold">{stat.value}</div></div>;
}